'use client';

import { useRef, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

export default function ContactSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('active');
        });
      },
      { threshold: 0.1 }
    );
    const elements = sectionRef?.current?.querySelectorAll('.reveal-up, .section-line');
    elements?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="contact"
      className="px-5 py-5"
      style={{ background: 'rgba(255,255,255,0.6)' }}>

      <div className="max-w-4xl mx-auto">
        <div className="section-line mb-8" />

        {/* Header */}
        <div className="text-center mb-14">
          <span className="text-xs font-bold uppercase tracking-[0.4em] mb-4 block reveal-up" style={{ color: '#16A34A' }}>
            Contacteer ons
          </span>
          <h2
            className="font-display leading-tight mb-4 reveal-up"
            style={{ fontSize: 'clamp(1.8rem,4vw,3rem)', fontWeight: 700, color: '#1A2B3C' }}>

            Heeft u een loodgieter{' '}
            <span className="font-display italic" style={{ color: '#16A34A', fontWeight: 400 }}>
              nodig?
            </span>
          </h2>
          <p className="text-sm max-w-md mx-auto reveal-up" style={{ color: '#7A95AD' }}>
            Antwoord gegarandeerd binnen 30 minuten. Gratis en vrijblijvende offerte.
          </p>
        </div>

        {/* Contact options */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
          {/* Phone */}
          <a
            href="tel:+32496538283"
            className="glass-card flex items-center gap-5 p-8 rounded-2xl group transition-all duration-500 reveal-up"
            style={{ border: '1px solid rgba(30,111,217,0.2)', textDecoration: 'none' }}>

            <div
              className="w-16 h-16 rounded-2xl flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform duration-300"
              style={{ background: '#16A34A' }}>

              <Icon name="PhoneIcon" size={28} className="text-white" />
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.15em] mb-1" style={{ color: '#16A34A' }}>
                Direct bellen
              </p>
              <p className="font-display font-bold text-2xl mb-1" style={{ color: '#1A2B3C' }}>0496 53 82 83</p>
              <p className="text-sm" style={{ color: '#7A95AD' }}>Beschikbaar 24u/24 · 7d/7</p>
            </div>
          </a>

          {/* WhatsApp */}
          <a
            href="https://wa.link/kusumk"
            target="_blank"
            rel="noopener noreferrer"
            className="glass-card flex items-center gap-5 p-8 rounded-2xl group transition-all duration-500 reveal-up"
            style={{ border: '1px solid rgba(34,197,94,0.2)', textDecoration: 'none' }}>

            <div
              className="w-16 h-16 rounded-2xl flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform duration-300"
              style={{ background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.25)' }}>

              <Icon name="ChatBubbleLeftRightIcon" size={28} style={{ color: '#16A34A' }} />
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.15em] mb-1" style={{ color: '#16A34A' }}>
                WhatsApp
              </p>
              <p className="font-display font-bold text-2xl mb-1" style={{ color: '#1A2B3C' }}>Stuur een bericht</p>
              <p className="text-sm" style={{ color: '#7A95AD' }}>Snel antwoord · Foto&apos;s toegestaan</p>
            </div>
          </a>
        </div>

        {/* Info row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8 reveal-up">
          <div
            className="glass-card rounded-xl p-5 flex items-start gap-3"
            style={{ border: '1px solid rgba(0,0,0,0.07)' }}>

            <Icon name="ClockIcon" size={18} style={{ color: '#16A34A' }} className="shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-semibold mb-1" style={{ color: '#1A2B3C' }}>Openingsuren</p>
              <p className="text-xs" style={{ color: '#7A95AD' }}>24u/24 · 7d/7<br />Spoedgevallen inbegrepen</p>
            </div>
          </div>
          <div
            className="glass-card rounded-xl p-5 flex items-start gap-3"
            style={{ border: '1px solid rgba(0,0,0,0.07)' }}>

            <Icon name="LockClosedIcon" size={18} style={{ color: '#16A34A' }} className="shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-semibold mb-1" style={{ color: '#1A2B3C' }}>Gegevensbescherming</p>
              <p className="text-xs" style={{ color: '#7A95AD' }}>Conform de AVG</p>
            </div>
          </div>
        </div>

        {/* Emergency CTA */}
        <div
          className="glass-card rounded-2xl p-8 text-center reveal-up"
          style={{ border: '1px solid rgba(30,111,217,0.2)', background: 'rgba(30,111,217,0.04)' }}>

          <div
            className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4"
            style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)' }}>

            <Icon name="BoltIcon" size={24} style={{ color: '#EF4444' }} />
          </div>
          <h3 className="font-display font-bold text-xl mb-2" style={{ color: '#1A2B3C' }}>Loodgieterspoedgeval?</h3>
          <p className="text-sm mb-6" style={{ color: '#7A95AD' }}>Wij interveniëren 24u/24, 7d/7. Bel ons direct voor een onmiddellijke interventie.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="tel:+32496538283"
              className="btn-primary shine-sweep flex items-center gap-3 px-8 py-4 rounded-full font-bold text-base w-full sm:w-auto justify-center">

              <Icon name="PhoneIcon" size={20} className="text-white" />
              0496 53 82 83
            </a>
            <a
              href="https://wa.link/kusumk"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 px-8 py-4 rounded-full font-bold text-base w-full sm:w-auto justify-center transition-all duration-300 hover:scale-105"
              style={{ background: 'rgba(34,197,94,0.12)', color: '#16A34A', border: '1px solid rgba(34,197,94,0.25)' }}>

              <Icon name="ChatBubbleLeftRightIcon" size={20} style={{ color: '#16A34A' }} />
              WhatsApp
            </a>
          </div>
        </div>
      </div>
    </section>);

}